import PodiumClient from '@podium/client'
import { useMatches } from '@remix-run/react'

export interface PodletsFunction<Data = unknown> {
    (args: { data: Data }): PodiumClient.PodiumClientResponse[];
}

type PodletsHandle = {
    [type: string]: PodiumClient.PodiumClientResponse;
};

export type PodletsHandles = PodletsFunction<PodletsHandle>;

type HandleWithPodlets = { podlets: PodletsHandles };

function isPodletHandle(handle: unknown): handle is HandleWithPodlets {
    return typeof handle === 'object' && handle !== null && 'podlets' in handle
}

/**
 * Looks for the "export let handle", that has "podlets" defined in a route
 * https://remix.run/docs/en/v1/route/handle
 */
function usePodlets(): PodiumClient.PodiumClientResponse[] {
    return useMatches().flatMap((match) => {
        let fn = isPodletHandle(match.handle) && match.handle?.podlets
        if (typeof fn !== 'function') return []
        return fn({ data: match.data as PodletsHandle })
    })
}

export function PodletLinks() {
    const podlets: PodiumClient.PodiumClientResponse[] = usePodlets()

    let cssLinks = podlets
        .filter((podlet) => podlet?.css !== undefined)
        .flatMap((podlet) => podlet?.css)

    return (
        <>
            {cssLinks.map((link) => (
                <link
                    href={link.value}
                    rel={link.rel as string | undefined}
                    key={JSON.stringify(link)}
                />
            ))}
        </>
    )
}

export function PodletScripts() {
    const podlets: PodiumClient.PodiumClientResponse[] = usePodlets()
    const scriptLinks = podlets
        .filter(podlet => podlet?.js !== undefined)
        .flatMap((podlet) => podlet.js)
    return (
        <>
            {scriptLinks.map((link) => (
                <script
                    src={link.value}
                    type={link.type as string | undefined}
                    key={JSON.stringify(link)}
                />
            ))}
        </>
    )
}
